import { useState, useEffect } from "react";
import { Link2, ShoppingBag, Sparkles, X } from "lucide-react";
import { useAuth } from "../AuthPage/AuthContext";
import axiosInstance from "../../APIs/axiosInstance";
import AddAffiliateLinkModal from "../Affiliate/AddAffiliateLinkModal";
import BuyTabContent from "./BuyTabContent";
import DiscoverTabContent from "./DiscoverTabContent";
import ConnectTabContent from "./ConnectTabContent";
import "./LinksAndSourcesSheet.css";

const TAKIT_COLOR = 'oklch(49.6% 0.265 301.924)';

const TABS = [
  { id: "discover", label: "Discover", Icon: Sparkles    },
  { id: "buy",      label: "Buy",      Icon: ShoppingBag },
  { id: "connect",  label: "Connect",  Icon: Link2       },
];

export default function LinksAndSourcesSheet({ postId, postAuthorId, links = [] }) {
  const { user } = useAuth();
  const isAuthor = !!(
    postAuthorId &&
    user?.id &&
    String(postAuthorId) === String(user.id)
  );

  const [isOpen, setIsOpen]   = useState(false);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const [activeTab, setActiveTab] = useState("discover");
  const [localLinks, setLocalLinks] = useState(links);
  const [affiliateModalOpen, setAffiliateModalOpen] = useState(false);

  // sync links prop → local state
  useEffect(() => { setLocalLinks(links); }, [links]);

  // responsive check
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // open/close animation
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), 380);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // refresh all links from API (after affiliate link added)
  const refreshLinks = async () => {
    try {
      const res = await axiosInstance.get(`posts/${postId}/`);
      setLocalLinks(res.data?.links || []);
    } catch {}
  };

  // Connect tab CRUD — optimistic
  const handleConnectAdd = async (label, url) => {
    const optimistic = { id: `temp-${Date.now()}`, label, url, is_affiliate: false };
    setLocalLinks((prev) => [...prev, optimistic]);
    try {
      const res = await axiosInstance.post(`posts/${postId}/links/`, { label, url });
      setLocalLinks((prev) =>
        prev.map((l) => (l.id === optimistic.id ? res.data : l))
      );
    } catch {
      setLocalLinks((prev) => prev.filter((l) => l.id !== optimistic.id));
    }
  };

  const handleConnectEdit = async (linkId, label, url) => {
    const previous = localLinks.find((l) => l.id === linkId);
    setLocalLinks((cur) =>
      cur.map((l) => (l.id === linkId ? { ...l, label, url } : l))
    );
    try {
      await axiosInstance.patch(`posts/${postId}/links/${linkId}/`, { label, url });
    } catch {
      setLocalLinks((cur) =>
        cur.map((l) => (l.id === linkId ? previous : l))
      );
    }
  };

  const handleConnectDelete = async (linkId) => {
    const removed = localLinks.find((l) => l.id === linkId);
    setLocalLinks((prev) => prev.filter((l) => l.id !== linkId));
    try {
      await axiosInstance.delete(`posts/${postId}/links/${linkId}/`);
    } catch {
      setLocalLinks((prev) => {
        const idx = prev.findIndex((l) => l.id === removed.id);
        const next = [...prev];
        next.splice(idx, 0, removed);
        return next;
      });
    }
  };

  const buyLinks     = localLinks.filter((l) => l.is_affiliate);
  const connectLinks = localLinks.filter((l) => !l.is_affiliate);
  const totalCount   = localLinks.length;

  const sheetStyle = isDesktop
    ? {
        transform: animating ? "scale(1)" : "scale(0.95)",
        opacity: animating ? 1 : 0,
        transition:
          "transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 200ms ease",
      }
    : {
        transform: animating ? "translateY(0)" : "translateY(100%)",
        transition: "transform 380ms cubic-bezier(0.32, 0.72, 0, 1)",
      };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lns-trigger-btn flex items-center gap-2 px-5 py-2.5
          font-bold text-sm tracking-wide text-white transition-all duration-200
          hover:scale-105 active:scale-95"
        style={{
          background: "linear-gradient(135deg, #8b3cf7 0%, #6d28d9 100%)",
          boxShadow: "0 4px 22px rgba(109,40,217,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
        }}
      >
        takeit
      </button>

      {/* Sheet overlay */}
      {visible && (
        <div
          className="fixed inset-0 z-[70] flex items-end md:items-center md:justify-center"
          onClick={() => setIsOpen(false)}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{
              backgroundColor: "rgba(0,0,0,0.65)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              opacity: animating ? 1 : 0,
            }}
          />

          {/* Sheet */}
          <div
            className="relative w-full md:max-w-[520px] md:mx-4"
            style={sheetStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="lns-sheet rounded-t-3xl md:rounded-3xl overflow-hidden flex flex-col"
              style={{ maxHeight: "80vh" }}
            >
              {/* Drag handle — mobile only */}
              <div className="flex justify-center pt-3 md:hidden shrink-0">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-4 pb-3 shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" style={{ color: TAKIT_COLOR }} />
                  <h2 className="text-white font-bold text-sm tracking-wide">Takit</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10
                    flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-white/50" />
                </button>
              </div>

              {/* Tab bar */}
              <div className="flex px-4 shrink-0 border-b border-white/[0.06]">
                {TABS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`lns-tab ${activeTab === id ? "lns-tab--active" : ""}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Tab content — scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto lns-scroll px-6 pt-5 pb-8">
                {activeTab === "buy" && (
                  <BuyTabContent
                    links={buyLinks}
                    isAuthor={isAuthor}
                    postId={postId}
                    onAddAffiliate={() => {
                      setIsOpen(false);
                      setTimeout(() => setAffiliateModalOpen(true), 420);
                    }}
                  />
                )}
                {activeTab === "discover" && <DiscoverTabContent />}
                {activeTab === "connect" && (
                  <ConnectTabContent
                    links={connectLinks}
                    isAuthor={isAuthor}
                    postId={postId}
                    onAdd={handleConnectAdd}
                    onEdit={handleConnectEdit}
                    onDelete={handleConnectDelete}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate modal — rendered at root z-index level */}
      <AddAffiliateLinkModal
        isOpen={affiliateModalOpen}
        onClose={() => setAffiliateModalOpen(false)}
        postId={postId}
        onSuccess={refreshLinks}
      />
    </>
  );
}
